import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserAuthContext } from '../../UserAuthContext';
import { apiUrl } from '../../config/api';
import sectorsData from '../../data/sectorsData';
import LocationFields from '../LocationFields';
import MyRequests from './MyRequests';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { moneyParams, trackEvent } from '../../utils/analytics';
import './QuoteRequestWidget.css';

const emptyForm = {
  materialName: '', sector: '', category: '', quantity: '',
  country: '', state: '', city: '', address: '', latitude: null, longitude: null,
  fulfillmentMethod: 'delivery', projectDimensions: '', budget: '', budgetCurrency: 'USD',
  preferredDeliveryDate: '',
};

export const isQuoteRequestWidgetVisible = (pathname = '') => {
  const localizedPath = pathname === '/es' ? '/' : pathname.replace(/^\/es(?=\/)/, '');
  const normalizedPath = localizedPath.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/sell/product/edit' || normalizedPath.startsWith('/sell/product/edit/')) {
    return false;
  }
  // Seller pages use their own layouts and stacking contexts, but buyers and
  // mixed buyer/seller accounts must retain access to the global quote action.
  if (normalizedPath === '/sell' || normalizedPath.startsWith('/sell/')) return true;
  return !normalizedPath.startsWith('/admin') && normalizedPath !== '/finances';
};

const QuoteRequestWidget = () => {
  const { user } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { currency, currencies } = useCurrency();
  const dialogRef = useRef(null);
  const imagesRef = useRef([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('form');
  const [form, setForm] = useState(() => ({ ...emptyForm, budgetCurrency: currency }));
  const [images, setImages] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const categories = useMemo(() => sectorsData[form.sector]?.categories || [], [form.sector]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setView('form');
  }, []);
  const openRequests = () => {
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      closePanel();
      return;
    }
    setView('requests');
  };

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => { if (event.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', closeOnEscape);
    setTimeout(() => dialogRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [closePanel, open]);

  useEffect(() => { imagesRef.current = images; }, [images]);
  useEffect(() => () => imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview)), []);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateLocation = (next) => setForm((current) => ({ ...current, ...next }));
  const addImages = (event) => {
    const selected = Array.from(event.target.files || []);
    setImages((current) => {
      const available = Math.max(0, 8 - current.length);
      return current.concat(selected.slice(0, available).map((file) => ({ file, preview: URL.createObjectURL(file) })));
    });
    event.target.value = '';
  };
  const removeImage = (index) => setImages((current) => current.filter((image, imageIndex) => {
    if (imageIndex === index) URL.revokeObjectURL(image.preview);
    return imageIndex !== index;
  }));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      closePanel();
      return;
    }
    if (!images.length) {
      setError('Add at least one image before creating your request.');
      return;
    }
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value == null ? '' : value));
    images.forEach(({ file }) => payload.append('images', file));
    setSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/quote-requests'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: payload,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'The request could not be created');
      // A sourcing request is the marketplace's lead: the buyer describes a
      // project and every verified seller can quote it. Only the declared
      // budget and the taxonomy travel - never the project address.
      trackEvent('generate_lead', {
        ...moneyParams(form.budgetCurrency, form.budget),
        item_category: form.sector || undefined,
        item_category2: form.category || undefined,
      });
      closePanel();
      setForm({ ...emptyForm, budgetCurrency: currency });
      images.forEach((image) => URL.revokeObjectURL(image.preview));
      setImages([]);
      navigate(`/orders/requests/${result.code || result.id}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return <>
    <button type="button" className="quote-widget__launcher" aria-expanded={open} onClick={() => { setView('form'); setOpen(true); }}>
      <span className="quote-widget__launcher-icon" aria-hidden="true" />
      <span>Click here to request a quote for your project!</span>
    </button>
    {open && <div className="quote-widget__backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closePanel(); }}>
      <section className={`quote-widget__panel${view === 'requests' ? ' quote-widget__panel--requests' : ''}`} role="dialog" aria-modal="true" aria-labelledby="quote-widget-title" ref={dialogRef} tabIndex="-1">
        <div className="quote-widget__heading">
          <div><h2 id="quote-widget-title">{view === 'requests' ? 'Requests center' : 'Request a quote'}</h2><p>{view === 'requests' ? 'Review and manage your project requests.' : 'Tell verified sellers what your project needs.'}</p></div>
          <div className="quote-widget__heading-actions">
            {view === 'form'
              ? <button type="button" className="quote-widget__requests-button" onClick={openRequests}>Check My Requests</button>
              : <button type="button" className="quote-widget__requests-button" onClick={() => setView('form')}>Request a Quote</button>}
            <button type="button" className="quote-widget__close" onClick={closePanel} aria-label="Close request panel">Close</button>
          </div>
        </div>
        {view === 'requests'
          ? <div className="quote-widget__requests"><MyRequests embedded onBeforeNavigate={closePanel} /></div>
          : <form onSubmit={submit} className="quote-widget__form">
          <div className="quote-widget__grid">
            <label className="quote-widget__wide"><span>Name of the Material/Product *</span><input required maxLength="180" value={form.materialName} onChange={(event) => update('materialName', event.target.value)} placeholder="Example: Reinforced concrete" /></label>
            <label><span>Sector *</span><select required value={form.sector} onChange={(event) => setForm((current) => ({ ...current, sector: event.target.value, category: '' }))}><option value="">Select a sector</option>{Object.keys(sectorsData).map((sector) => <option key={sector} value={sector}>{t(sector)}</option>)}</select></label>
            <label><span>Category *</span><select required disabled={!form.sector} value={form.category} onChange={(event) => update('category', event.target.value)}><option value="">Select a category</option>{categories.map((category) => <option key={category} value={category}>{t(category)}</option>)}</select></label>
            <label className="quote-widget__wide"><span>Quantity *</span><input required type="number" inputMode="numeric" min="1" step="1" value={form.quantity} onKeyDown={(event) => { if (['e', 'E', '+', '-', '.'].includes(event.key)) event.preventDefault(); }} onChange={(event) => update('quantity', event.target.value.replace(/\D/g, ''))} placeholder="Example: 250" /></label>
          </div>

          <fieldset><legend>Project address</legend><LocationFields required value={form} onChange={updateLocation} /><label className="quote-widget__address"><span>Address or specific location *</span><input required maxLength="1000" value={form.address} onChange={(event) => update('address', event.target.value)} placeholder="Street, building, site reference or pickup point" /></label></fieldset>

          <fieldset><legend>Fulfillment *</legend><div className="quote-widget__choices"><label><input type="radio" name="fulfillmentMethod" value="pickup" checked={form.fulfillmentMethod === 'pickup'} onChange={(event) => update('fulfillmentMethod', event.target.value)} /><span><strong>Pick up</strong><small>I will collect the products.</small></span></label><label><input type="radio" name="fulfillmentMethod" value="delivery" checked={form.fulfillmentMethod === 'delivery'} onChange={(event) => update('fulfillmentMethod', event.target.value)} /><span><strong>Delivery</strong><small>Deliver to the project address.</small></span></label></div></fieldset>

          <div className="quote-widget__grid">
            <label><span>Project dimensions <small>Optional</small></span><input maxLength="240" value={form.projectDimensions} onChange={(event) => update('projectDimensions', event.target.value)} placeholder="Area, length, height or volume" /></label>
            <label><span>{t('Budget')} <small>{t('Optional')}</small></span><div className="quote-widget__money"><select aria-label={t('Budget currency')} value={form.budgetCurrency} onChange={(event) => update('budgetCurrency', event.target.value)}>{currencies.map((code) => <option key={code} value={code}>{code}</option>)}</select><input type="number" min="0" step="0.01" value={form.budget} onChange={(event) => update('budget', event.target.value)} placeholder="0.00" /></div></label>
          </div>

          <fieldset><legend>Project images *</legend><p className="quote-widget__hint">Add at least one JPG, PNG or WebP image. Up to 8 images, 8 MB each.</p><label className="quote-widget__upload"><input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={addImages} /><span>Add images</span></label>{images.length > 0 && <div className="quote-widget__previews">{images.map((image, index) => <figure key={`${image.file.name}-${image.preview}`}><img src={image.preview} alt={`Project upload ${index + 1}`} /><button type="button" onClick={() => removeImage(index)}>Remove</button></figure>)}</div>}</fieldset>

          <div className="quote-widget__grid">
            <label><span>{t('Preferred delivery date')} <small>{t('Optional')}</small></span><input type="date" min={new Date().toISOString().slice(0, 10)} value={form.preferredDeliveryDate} onChange={(event) => update('preferredDeliveryDate', event.target.value)} /></label>
          </div>

          {error && <p className="quote-widget__error" role="alert">{error}</p>}
          <button className="quote-widget__submit" type="submit" disabled={submitting}>{!user ? 'Log in to create a request' : submitting ? 'Creating request...' : 'Create a Request'}</button>
          {user && <p className="quote-widget__expiry">Requests remain active for 7 days. Weekly limits depend on your Eulua plan.</p>}
          </form>}
      </section>
    </div>}
  </>;
};

export default QuoteRequestWidget;
